import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

const Pagination = ({
  currentPage,
  totalPages,
  startItem,
  endItem,
  totalItems,
  itemsPerPage,
  onItemsPerPageChange,
  onPageChange,
  itemsPerPageOptions = [10,20, 30, 50, 100],
}) => {
  // Determine which page numbers to display for navigation
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; // Maximum number of page buttons to show

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, totalPages - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  if (totalItems === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        alignItems: "center",
        width: "100%",
      }}
    >
      {/* Pagination Info */}
      <div style={{ fontSize: "14px", color: "#6c757d" }}>
        Showing {startItem}-{endItem} of {totalItems} items
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        {/* Items per page selector */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label htmlFor="itemsPerPage" style={{ margin: 0, fontSize: "14px" }}>
            Items per page:
          </label>
          <select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            style={{
              padding: "4px 8px",
              fontSize: "14px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <nav aria-label="Page navigation">
            <ul
              style={{
                display: "flex",
                listStyle: "none",
                padding: 0,
                margin: 0,
                gap: "4px",
              }}
            >
              {/* First Page button */}
              <li>
                <button
                  onClick={() => onPageChange(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                  title="First page"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "34px",
                    height: "34px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background: currentPage === 1 ? "#f8f9fa" : "#fff",
                    color: currentPage === 1 ? "#aaa" : "#000",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronsLeft size={16} />
                </button>
              </li>

              {/* Previous Page button */}
              <li>
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  title="Previous page"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "34px",
                    height: "34px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background: currentPage === 1 ? "#f8f9fa" : "#fff",
                    color: currentPage === 1 ? "#aaa" : "#000",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
              </li>

              {/* Page number buttons */}
              {pageNumbers.map((page) => (
                <li key={page}>
                  <button
                    onClick={() => onPageChange(page)}
                    aria-current={page === currentPage ? "page" : undefined}
                    style={{
                      minWidth: "34px",
                      height: "34px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      background: page === currentPage ? "#007bff" : "#fff",
                      color: page === currentPage ? "#fff" : "#000",
                      cursor: "pointer",
                    }}
                  >
                    {page}
                  </button>
                </li>
              ))}

              {/* Next Page button */}
              <li>
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  title="Next page"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "34px",
                    height: "34px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background:
                      currentPage === totalPages ? "#f8f9fa" : "#fff",
                    color: currentPage === totalPages ? "#aaa" : "#000",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </li>

              {/* Last Page button */}
              <li>
                <button
                  onClick={() => onPageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last page"
                  title="Last page"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "34px",
                    height: "34px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background:
                      currentPage === totalPages ? "#f8f9fa" : "#fff",
                    color: currentPage === totalPages ? "#aaa" : "#000",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronsRight size={16} />
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
};

export default Pagination;
